import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Shuffle, 
  ArrowLeft, 
  Loader2, 
  QrCode,
  Download,
  Sticker,
  Circle,
  Square,
  Package,
  FileText,
  Shield,
  CreditCard,
  Check,
  Clock,
  Upload,
  Trash2,
  AlertTriangle
} from "lucide-react";
import { toast } from "sonner";
import { drawsAPI, gamesAPI, qrcodeAPI, stickerOrdersAPI, legalServicesAPI } from "../lib/api";
import api from "../lib/api";

// Sticker Preview Component
const StickerPreview = ({ format, backgroundColor, textColor, accentColor, slogan, logoUrl, qrCode }) => {
  const isRound = format === "round_7cm";
  
  return (
    <div 
      className={`relative flex flex-col items-center justify-center p-4 ${
        isRound ? "w-48 h-48 rounded-full" : "w-56 h-36 rounded-xl"
      }`}
      style={{ backgroundColor }}
    >
      {/* Bleed indicator */}
      <div 
        className={`absolute inset-0 border-2 border-dashed border-pink-500 ${
          isRound ? "rounded-full" : "rounded-xl"
        }`}
        style={{ margin: "6px" }}
      />
      
      {/* Content */}
      <div className="relative z-10 text-center">
        {logoUrl && (
          <img src={logoUrl} alt="Logo" className="h-8 mx-auto mb-2 object-contain" />
        )}
        <p 
          className="font-bold text-sm mb-2 line-clamp-2" 
          style={{ color: textColor }}
        >
          {slogan || "Votre slogan ici"}
        </p>
        {qrCode && (
          <div className="bg-white p-1 rounded inline-block">
            <QRCodeSVG value={qrCode} size={isRound ? 50 : 40} />
          </div>
        )}
      </div>
      
      {/* Accent decoration */}
      <div 
        className={`absolute ${isRound ? "bottom-4" : "bottom-2"} left-1/2 -translate-x-1/2 h-1 w-12 rounded-full`}
        style={{ backgroundColor: accentColor }}
      />
    </div>
  );
};

export default function ServicesPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [serviceConfig, setServiceConfig] = useState(null);
  
  // Sticker form
  const [stickerForm, setStickerForm] = useState({
    format: "round_7cm",
    quantity: 10,
    logo_url: "",
    slogan: "",
    background_color: "#1E293B",
    text_color: "#FFFFFF",
    accent_color: "#F59E0B"
  });
  const [creatingSticker, setCreatingSticker] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const stickerLogoRef = useRef(null);
  
  // Legal service form
  const [legalForm, setLegalForm] = useState({
    service_type: "regulation",
    draw_date: "",
    additional_info: ""
  });
  const [creatingLegal, setCreatingLegal] = useState(false);
  const [draws, setDraws] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [gamesRes, drawsRes, configRes] = await Promise.all([
        gamesAPI.list().catch(() => ({ data: [] })),
        drawsAPI.list().catch(() => ({ data: [] })),
        api.services.getConfig().catch(() => ({ data: {
          qrcode_enabled: true,
          stickers_enabled: true,
          legal_regulation_enabled: true,
          legal_bailiff_enabled: true,
          legal_bundle_enabled: true
        }}))
      ]);
      
      const allItems = [
        ...gamesRes.data.map(g => ({ ...g, type: "game" })),
        ...drawsRes.data.map(d => ({ ...d, type: "draw" }))
      ];
      setItems(allItems);
      setDraws(drawsRes.data.filter(d => d.status !== "completed"));
      setServiceConfig(configRes.data);
      
      if (allItems.length > 0) {
        setSelectedItem(allItems[0]);
        loadQrCode(allItems[0]);
      }
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const loadQrCode = async (item) => {
    if (!item) return;
    try {
      const res = await qrcodeAPI.generate(item.type, item.id);
      setQrCodeData(res.data);
      setStickerForm(prev => ({ ...prev, slogan: item.title }));
    } catch (error) {
      console.error("QR code error", error);
    }
  };

  const handleItemChange = (itemId) => {
    const item = items.find(i => i.id === itemId);
    setSelectedItem(item);
    loadQrCode(item);
  };

  const handleUploadStickerLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setStickerForm({...stickerForm, logo_url: res.data.url});
      toast.success("Logo uploadé");
    } catch (error) {
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploadingLogo(false);
      if (stickerLogoRef.current) {
        stickerLogoRef.current.value = "";
      }
    }
  };

  const downloadQrCode = () => {
    if (!qrCodeData) return;
    
    const link = document.createElement("a");
    link.href = qrCodeData.qr_code;
    link.download = `qrcode_${selectedItem?.title || "tireo"}.png`;
    link.click();
    toast.success("QR code téléchargé");
  };

  const handleCreateStickerOrder = async () => {
    if (!selectedItem) {
      toast.error("Sélectionnez un jeu ou tirage");
      return;
    }
    
    setCreatingSticker(true);
    try {
      const res = await stickerOrdersAPI.create({
        item_type: selectedItem.type,
        item_id: selectedItem.id,
        ...stickerForm
      });
      toast.success("Commande créée");
      navigate(`/sticker-orders/${res.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la commande");
    } finally {
      setCreatingSticker(false);
    }
  };

  const handleCreateLegalOrder = async () => {
    if (!legalForm.draw_id) {
      toast.error("Sélectionnez un tirage");
      return;
    }
    
    setCreatingLegal(true);
    try {
      const res = await legalServicesAPI.create({
        ...legalForm
      });
      toast.success("Commande créée");
      navigate(`/legal-services/${res.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la commande");
    } finally {
      setCreatingLegal(false);
    }
  };

  // Dynamic sticker prices from config
  const stickerPrices = serviceConfig ? {
    5: serviceConfig.sticker_price_5 || 15,
    10: serviceConfig.sticker_price_10 || 25,
    50: serviceConfig.sticker_price_50 || 80,
    100: serviceConfig.sticker_price_100 || 140
  } : { 5: 15, 10: 25, 50: 80, 100: 140 };

  // Dynamic legal service prices
  const legalPrices = serviceConfig ? {
    regulation: serviceConfig.regulation_price || 99,
    bailiff: serviceConfig.bailiff_price || 199,
    bundle: serviceConfig.bundle_price || 249
  } : { regulation: 99, bailiff: 199, bundle: 249 };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-3 sm:gap-4">
          <Link to="/dashboard">
            <Button variant="ghost" size="icon" className="shrink-0">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shrink-0">
              <Package className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg sm:text-xl font-bold text-foreground" style={{ fontFamily: 'Outfit' }}>Services</span>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Check if any service is available */}
        {serviceConfig && !serviceConfig.qrcode_enabled && !serviceConfig.stickers_enabled && 
         !serviceConfig.legal_regulation_enabled && !serviceConfig.legal_bailiff_enabled ? (
          <Card className="bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800">
            <CardContent className="p-8 text-center">
              <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-amber-700 dark:text-amber-400 mb-2">
                Services temporairement indisponibles
              </h2>
              <p className="text-amber-600 dark:text-amber-500">
                Les services additionnels ne sont pas disponibles pour le moment. Revenez plus tard.
              </p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => navigate("/dashboard")}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour au tableau de bord
              </Button>
            </CardContent>
          </Card>
        ) : (
        <Tabs defaultValue={serviceConfig?.qrcode_enabled ? "qrcode" : serviceConfig?.stickers_enabled ? "stickers" : "legal"} className="space-y-6">
          <TabsList className="bg-card w-full justify-start overflow-x-auto">
            {serviceConfig?.qrcode_enabled && (
              <TabsTrigger value="qrcode" className="text-xs sm:text-sm whitespace-nowrap">
                <QrCode className="w-4 h-4 mr-1 sm:mr-2" />
                QR Code
              </TabsTrigger>
            )}
            {serviceConfig?.stickers_enabled && (
              <TabsTrigger value="stickers" className="text-xs sm:text-sm whitespace-nowrap">
                <Sticker className="w-4 h-4 mr-1 sm:mr-2" />
                Stickers
              </TabsTrigger>
            )}
            {(serviceConfig?.legal_regulation_enabled || serviceConfig?.legal_bailiff_enabled || serviceConfig?.legal_bundle_enabled) && (
              <TabsTrigger value="legal" className="text-xs sm:text-sm whitespace-nowrap">
                <FileText className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Services </span>juridiques
              </TabsTrigger>
            )}
          </TabsList>

          {/* QR Code Tab */}
          {serviceConfig?.qrcode_enabled && (
          <TabsContent value="qrcode">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-card">
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Outfit' }}>Générer un QR Code</CardTitle>
                  <CardDescription>
                    Téléchargez le QR code pour vos supports de communication
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Sélectionner un jeu ou tirage</Label>
                    <Select 
                      value={selectedItem?.id || ""} 
                      onValueChange={handleItemChange}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choisir..." />
                      </SelectTrigger>
                      <SelectContent>
                        {items.map((item) => (
                          <SelectItem key={item.id} value={item.id}>
                            {item.title} ({item.type === "game" ? "Jeu" : "Tirage"})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {qrCodeData && (
                    <Button onClick={downloadQrCode} className="w-full rounded-full">
                      <Download className="w-4 h-4 mr-2" />
                      Télécharger le QR Code
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* QR Preview */}
              <Card className="bg-card">
                <CardContent className="p-8 flex flex-col items-center justify-center">
                  {qrCodeData ? (
                    <>
                      <img 
                        src={qrCodeData.qr_code} 
                        alt="QR Code" 
                        className="w-48 h-48 mb-4"
                      />
                      <p className="text-sm text-muted-foreground text-center">
                        {qrCodeData.item_title}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {qrCodeData.url}
                      </p>
                    </>
                  ) : (
                    <div className="text-center text-muted-foreground">
                      <QrCode className="w-16 h-16 mx-auto mb-4 opacity-30" />
                      <p>Sélectionnez un élément pour générer le QR code</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          )}

          {/* Stickers Tab */}
          {serviceConfig?.stickers_enabled && (
          <TabsContent value="stickers">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Config */}
              <div className="lg:col-span-2 space-y-6">
                <Card className="bg-card">
                  <CardHeader>
                    <CardTitle style={{ fontFamily: 'Outfit' }}>Commander des stickers</CardTitle>
                    <CardDescription>
                      Personnalisez et commandez vos stickers avec QR code
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Item Selection */}
                    <div className="space-y-2">
                      <Label>Jeu ou tirage associé</Label>
                      <Select 
                        value={selectedItem?.id || ""} 
                        onValueChange={handleItemChange}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choisir..." />
                        </SelectTrigger>
                        <SelectContent>
                          {items.map((item) => (
                            <SelectItem key={item.id} value={item.id}>
                              {item.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Format */}
                    <div className="space-y-3">
                      <Label>Format</Label>
                      <RadioGroup
                        value={stickerForm.format}
                        onValueChange={(v) => setStickerForm({...stickerForm, format: v})}
                        className="grid grid-cols-2 gap-4"
                      >
                        <Label
                          htmlFor="round"
                          className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer ${
                            stickerForm.format === "round_7cm" ? "border-primary bg-primary/5" : "border-border"
                          }`}
                        >
                          <RadioGroupItem value="round_7cm" id="round" className="sr-only" />
                          <Circle className="w-8 h-8 text-primary" />
                          <div>
                            <p className="font-medium">Rond</p>
                            <p className="text-sm text-muted-foreground">Ø 7cm</p>
                          </div>
                        </Label>
                        <Label
                          htmlFor="rect"
                          className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer ${
                            stickerForm.format === "rectangle" ? "border-primary bg-primary/5" : "border-border"
                          }`}
                        >
                          <RadioGroupItem value="rectangle" id="rect" className="sr-only" />
                          <Square className="w-8 h-8 text-primary" />
                          <div>
                            <p className="font-medium">Rectangle</p>
                            <p className="text-sm text-muted-foreground">8 × 5 cm</p>
                          </div>
                        </Label>
                      </RadioGroup>
                    </div>

                    {/* Quantity */}
                    <div className="space-y-3">
                      <Label>Quantité</Label>
                      <RadioGroup
                        value={String(stickerForm.quantity)}
                        onValueChange={(v) => setStickerForm({...stickerForm, quantity: parseInt(v)})}
                        className="grid grid-cols-4 gap-3"
                      >
                        {Object.entries(stickerPrices).map(([qty, price]) => (
                          <Label
                            key={qty}
                            className={`flex flex-col items-center p-3 rounded-xl border-2 cursor-pointer ${
                              stickerForm.quantity === parseInt(qty) ? "border-primary bg-primary/5" : "border-border"
                            }`}
                          >
                            <RadioGroupItem value={qty} className="sr-only" />
                            <span className="text-xl font-bold">{qty}</span>
                            <span className="text-sm text-muted-foreground">{price}€</span>
                          </Label>
                        ))}
                      </RadioGroup>
                    </div>

                    {/* Customization */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Slogan</Label>
                        <Input
                          placeholder="Tentez votre chance !"
                          value={stickerForm.slogan}
                          onChange={(e) => setStickerForm({...stickerForm, slogan: e.target.value})}
                          className="bg-slate-50 border-transparent"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Logo</Label>
                        <input
                          type="file"
                          ref={stickerLogoRef}
                          className="hidden"
                          accept="image/*"
                          onChange={handleUploadStickerLogo}
                        />
                        {stickerForm.logo_url ? (
                          <div className="relative group w-fit">
                            <img 
                              src={stickerForm.logo_url} 
                              alt="Logo" 
                              className="h-10 object-contain rounded"
                            />
                            <button
                              type="button"
                              onClick={() => setStickerForm({...stickerForm, logo_url: ""})}
                              className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => stickerLogoRef.current?.click()}
                            disabled={uploadingLogo}
                            className="bg-slate-50 border-transparent"
                          >
                            {uploadingLogo ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Upload className="w-4 h-4 mr-2" />
                            )}
                            Uploader
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Colors */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs">Fond</Label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={stickerForm.background_color}
                            onChange={(e) => setStickerForm({...stickerForm, background_color: e.target.value})}
                            className="w-10 h-10 p-1 cursor-pointer"
                          />
                          <Input
                            value={stickerForm.background_color}
                            onChange={(e) => setStickerForm({...stickerForm, background_color: e.target.value})}
                            className="bg-slate-50 border-transparent text-xs"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Texte</Label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={stickerForm.text_color}
                            onChange={(e) => setStickerForm({...stickerForm, text_color: e.target.value})}
                            className="w-10 h-10 p-1 cursor-pointer"
                          />
                          <Input
                            value={stickerForm.text_color}
                            onChange={(e) => setStickerForm({...stickerForm, text_color: e.target.value})}
                            className="bg-slate-50 border-transparent text-xs"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Accent</Label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={stickerForm.accent_color}
                            onChange={(e) => setStickerForm({...stickerForm, accent_color: e.target.value})}
                            className="w-10 h-10 p-1 cursor-pointer"
                          />
                          <Input
                            value={stickerForm.accent_color}
                            onChange={(e) => setStickerForm({...stickerForm, accent_color: e.target.value})}
                            className="bg-slate-50 border-transparent text-xs"
                          />
                        </div>
                      </div>
                    </div>

                    <Button
                      onClick={handleCreateStickerOrder}
                      disabled={creatingSticker || !selectedItem}
                      className="w-full rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
                    >
                      {creatingSticker ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <CreditCard className="w-4 h-4 mr-2" />
                      )}
                      Commander {stickerForm.quantity} stickers - {stickerPrices[stickerForm.quantity]}€
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Preview */}
              <div>
                <Card className="bg-card sticky top-24">
                  <CardHeader>
                    <CardTitle className="text-base">Aperçu</CardTitle>
                  </CardHeader>
                  <CardContent className="flex justify-center">
                    <StickerPreview
                      format={stickerForm.format}
                      backgroundColor={stickerForm.background_color}
                      textColor={stickerForm.text_color}
                      accentColor={stickerForm.accent_color}
                      slogan={stickerForm.slogan}
                      logoUrl={stickerForm.logo_url}
                      qrCode={qrCodeData?.url || "https://tireo.app"}
                    />
                  </CardContent>
                  <div className="px-6 pb-6">
                    <p className="text-xs text-muted-foreground text-center">
                      Trait rose = découpe (fond perdu 3mm)
                    </p>
                  </div>
                </Card>
              </div>
            </div>
          </TabsContent>
          )}

          {/* Legal Services Tab */}
          {(serviceConfig?.legal_regulation_enabled || serviceConfig?.legal_bailiff_enabled || serviceConfig?.legal_bundle_enabled) && (
          <TabsContent value="legal">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Regulation Service */}
              {serviceConfig?.legal_regulation_enabled && (
              <Card className="bg-card">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                      <FileText className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle style={{ fontFamily: 'Outfit' }}>Rédaction règlement</CardTitle>
                      <CardDescription>Par un juriste professionnel</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      Règlement conforme à la loi
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      Envoyé par email sous 48h
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      Révisions incluses
                    </li>
                  </ul>
                  
                  <div className="pt-4 border-t">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-2xl font-bold text-foreground">{legalPrices.regulation}€</span>
                      <Badge variant="secondary">HT</Badge>
                    </div>
                    
                    <div className="space-y-3">
                      <Select 
                        value={legalForm.draw_id || ""} 
                        onValueChange={(v) => setLegalForm({...legalForm, draw_id: v, service_type: "regulation"})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner un tirage" />
                        </SelectTrigger>
                        <SelectContent>
                          {draws.map((draw) => (
                            <SelectItem key={draw.id} value={draw.id}>
                              {draw.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      <Button
                        onClick={() => {
                          setLegalForm({...legalForm, service_type: "regulation"});
                          handleCreateLegalOrder();
                        }}
                        disabled={creatingLegal || !legalForm.draw_id}
                        className="w-full rounded-full"
                      >
                        {creatingLegal ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <CreditCard className="w-4 h-4 mr-2" />
                        )}
                        Commander
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
              )}

              {/* Bailiff Service */}
              {serviceConfig?.legal_bailiff_enabled && (
              <Card className="bg-card">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                      <Shield className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <CardTitle style={{ fontFamily: 'Outfit' }}>Contrôle huissier</CardTitle>
                      <CardDescription>Supervision officielle du tirage</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      Validation de la date de tirage
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      Procès-verbal officiel
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      Attestation légale
                    </li>
                  </ul>
                  
                  <div className="pt-4 border-t">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-2xl font-bold text-foreground">{legalPrices.bailiff}€</span>
                      <Badge variant="secondary">HT</Badge>
                    </div>
                    
                    <div className="space-y-3">
                      <Select 
                        value={legalForm.draw_id || ""} 
                        onValueChange={(v) => setLegalForm({...legalForm, draw_id: v, service_type: "bailiff"})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner un tirage" />
                        </SelectTrigger>
                        <SelectContent>
                          {draws.map((draw) => (
                            <SelectItem key={draw.id} value={draw.id}>
                              {draw.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      <Input
                        type="datetime-local"
                        placeholder="Date du tirage"
                        value={legalForm.draw_date}
                        onChange={(e) => setLegalForm({...legalForm, draw_date: e.target.value})}
                        className="bg-slate-50 border-transparent"
                      />
                      
                      <Button
                        onClick={() => {
                          setLegalForm({...legalForm, service_type: "bailiff"});
                          handleCreateLegalOrder();
                        }}
                        disabled={creatingLegal || !legalForm.draw_id}
                        className="w-full rounded-full bg-amber-500 hover:bg-amber-600"
                      >
                        {creatingLegal ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <CreditCard className="w-4 h-4 mr-2" />
                        )}
                        Commander
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
              )}
            </div>

            {/* Info Box */}
            <Card className="bg-blue-50 border-blue-200 mt-6 dark:bg-blue-950/30 dark:border-blue-800">
              <CardContent className="p-4 flex items-start gap-3">
                <Clock className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-900 dark:text-blue-100">Comment ça marche ?</p>
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    Après paiement, notre juriste vous contacte sous 24h pour les détails. 
                    Le règlement est rédigé et envoyé par email. Si vous avez commandé le service huissier, 
                    la date du tirage sera validée et l'huissier supervisera le tirage en ligne.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          )}
        </Tabs>
        )}
      </main>
    </div>
  );
}
